import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Chip, LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material';
import { RelationshipsIcon } from '../global/CustomIcons';

import { fetchRequest, clearState } from '../../redux/actions/discovery/findSimilar';
import { useDisplayNames } from '../../utils/displayNames';

/**
 * "Signals that behave like this one" — the user-facing consumer of the
 * E9 embedding space (the same index that powers recall and shock
 * analogues). Nearest neighbours in learned-shape space; similarity is
 * descriptive, never evidence. Renders nothing destructive when the
 * index is down or the signal isn't embedded yet — it just says so.
 */
const SimilarSignalsSection = ({ sourceKey, k = 8 }) => {
  const dispatch = useDispatch();
  const state = useSelector((s) => s.discovery_find_similar);
  const nameOf = useDisplayNames();

  useEffect(() => {
    if (sourceKey) {
      dispatch(fetchRequest({ kind: 'signal', key: sourceKey, k }));
    }
    return () => { dispatch(clearState()); };
  }, [dispatch, sourceKey, k]);

  const resp = state?.response;
  const isLoading = Boolean(state?.isLoading);
  const results = resp?.results || [];
  const unavailable = Boolean(resp?.unavailable);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <RelationshipsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Signals that behave like this
        </Typography>
        <Tooltip
          arrow
          title="Nearest neighbours in the learned embedding space — descriptive similarity, never used as evidence. Same index that powers shock analogues."
        >
          <Chip
            size="small"
            variant="outlined"
            label={resp?.embedding_version ? `v${resp.embedding_version}` : 'embeddings'}
          />
        </Tooltip>
      </Stack>

      {isLoading && <LinearProgress sx={{ my: 1, borderRadius: 2 }} />}

      {!isLoading && unavailable && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          {resp?.message || 'The similarity index is not reachable right now.'}
        </Typography>
      )}

      {!isLoading && !unavailable && results.length === 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          No similar signals indexed yet — the embedding index fills in as the
          nightly backfill runs.
        </Typography>
      )}

      {!isLoading && !unavailable && results.length > 0 && (
        <Stack spacing={0.5}>
          {results.map((r) => (
            <Stack key={r.key} direction="row" spacing={1.5} alignItems="center">
              <Typography
                variant="caption"
                noWrap
                sx={{ flexGrow: 1, minWidth: 0 }}
                title={r.key}
              >
                {nameOf(r.key)}
              </Typography>
              <Box sx={{ width: 80, flexShrink: 0 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, Math.round((r.similarity || 0) * 100)))}
                  sx={{ height: 5, borderRadius: 2 }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ width: 40, textAlign: 'right' }}
              >
                {Math.round((r.similarity || 0) * 100)}%
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default SimilarSignalsSection;
